import React from 'react';
import { InfoRowProps } from 'components/infoRow/types';
import {
  AcknowledgmentStatus,
  Localize,
  SelfOnboardingDocumentFormData,
} from 'containers/selfOnboarding/types';
import { getDocumentInputsConfig } from 'containers/selfOnboarding/steps/documentStep';
import { camelToTitleCase, objectEntries } from 'utils';
import { getPrettyFormattedDate } from 'utils/dates';
import { Badge } from '@odin-labs/components';

type DocumentInfoField = keyof Required<
  Omit<
    SelfOnboardingDocumentFormData,
    | 'key'
    | 'workerDocTypeKey'
    | 'versionId'
    | 'acknowledgmentFile'
    | 'frontFile'
    | 'backFile'
    | 'acknowledgmentProgress'
    | 'documentType'
    | 'multiInstanceDocTitle'
  >
>;
export type DocumentInfoRowConfig = Omit<
  InfoRowProps,
  'onEditClick' | 'allowEdit'
>;

export function getInfoRows(
  document: SelfOnboardingDocumentFormData,
  localize: Localize,
): DocumentInfoRowConfig[] {
  const {
    type,
    stateIssued,
    number,
    issueDate,
    expirationDate,
    isTrainingConnectCard,
    acknowledgmentStatus,
    acknowledgmentDate,
    workerDocTypeKey,
  } = document;
  const { visibleKeys, typeCopy } = getDocumentInputsConfig(workerDocTypeKey);

  const documentFields: Record<DocumentInfoField, DocumentInfoRowConfig> = {
    type: { label: localize(typeCopy), value: type?.label },
    stateIssued: {
      label: camelToTitleCase('stateIssued'),
      value: stateIssued?.label,
    },
    number: { label: camelToTitleCase('number'), value: number },
    issueDate: { label: camelToTitleCase('issueDate'), value: issueDate },
    expirationDate: {
      label: camelToTitleCase('expirationDate'),
      value: expirationDate,
    },
    isTrainingConnectCard: {
      label: camelToTitleCase('isTrainingConnectCard'),
      value: isTrainingConnectCard ? 'Yes' : 'No',
    },
    acknowledgmentStatus: {
      label: camelToTitleCase('status'),
      value: acknowledgmentStatus?.includes(AcknowledgmentStatus.Confirmed) ? (
        <Badge size="lg" text="Complete" color="green" />
      ) : (
        <Badge size="lg" text="Incomplete" color="yellow" />
      ),
    },
    acknowledgmentDate: {
      label: 'Acknowledged On',
      value: getPrettyFormattedDate(acknowledgmentDate),
    },
  };

  return objectEntries(documentFields)
    .map(([fieldKey, docInfoRowConfig]): DocumentInfoRowConfig | null => {
      if (
        visibleKeys.includes(fieldKey as typeof visibleKeys[number]) ||
        (fieldKey === 'acknowledgmentDate' && acknowledgmentDate)
      ) {
        return docInfoRowConfig;
      }
      return null;
    })
    .filter(Boolean);
}
